import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { ACCESS_TOKEN_KEY, UPLOAD_HASH_KEY, UPLOAD_ID_KEY } from '../common/constants/persistentDataConstants';

const UPLOAD_SERVER_URL = process.env.REACT_APP_UPLOAD_SERVER_URL;

export interface UploadImagesVariables {
    files: File[];
}

export interface UploadApiResponse {
    status: number | string;
    status_message: string;
    data: string;
}

export type UploadImagesResponse = boolean;

const TARGET_FILE_SIZE = 200 * 1024;
const MAX_DIMENSION = 1200;

async function compressImage(file: File): Promise<File> {
    if (file.size <= TARGET_FILE_SIZE) {
        return file;
    }

    const imageBitmap = await createImageBitmap(file);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');

    let { width, height } = imageBitmap;

    if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const scale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(imageBitmap, 0, 0, width, height);

    let quality = 0.7;      // start lower
    let blob: Blob | null = null;

    while (quality >= 0.25) {
        blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob(resolve, 'image/jpeg', quality)
        );

        if (blob && blob.size <= TARGET_FILE_SIZE) break;
        quality -= 0.05;
    }

    if (!blob) throw new Error('Image compression failed');

    return new File([blob], file.name, { type: 'image/jpeg' });
}



async function uploadImagesFetcher(variables: UploadImagesVariables): Promise<UploadImagesResponse> {
    const { files } = variables;

    const uploadId = sessionStorage.getItem(UPLOAD_ID_KEY);
    const uploadHashKey = sessionStorage.getItem(UPLOAD_HASH_KEY);
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);

    if (!uploadId) throw new Error("");
    if (!token) throw new Error("");
    if (!uploadHashKey) throw new Error("");
    if (!UPLOAD_SERVER_URL) throw new Error("");

    const validFiles = files.filter(file => file && file.type === 'image/jpeg');

    const compressedFiles = await Promise.all(
        validFiles.map((file) => compressImage(file))
    );

    const formData = new FormData();
    formData.append("uploadId", uploadId);
    formData.append("token", token);
    formData.append("uploadHash", uploadHashKey);
    compressedFiles.forEach((file) => formData.append("image[]", file));

    const requestOptions: RequestInit = {
        method: 'POST',
        body: formData,
    };

    try {
        const response = await fetch(UPLOAD_SERVER_URL, requestOptions);
        const responseData: UploadApiResponse = await response.json();
        if (!response.ok) {
            const errorMessage = responseData.data || responseData.status_message;
            throw new Error(errorMessage);
        }
        if (responseData.status === 200 || String(responseData.status) === "200") {
            return true;
        }

    } catch (error: any) {
        if (error instanceof Error) {
            throw error;
        } else {
            throw new Error(String(error));
        }
    }

    return false;
}

export function useUploadImages(
  options?: Omit<UseMutationOptions<UploadImagesResponse, Error, UploadImagesVariables, unknown>, 'mutationFn'>
) {
    return useMutation<UploadImagesResponse, Error, UploadImagesVariables, unknown>({
        mutationFn: uploadImagesFetcher,
        ...options,
    });
}
